<template>
    <section class="hero-sticky" :class="{ released: finished }">
        <div class="solid-bg" :style="bgStyle"></div>

        <div
            class="video-mask"
            :class="{ released: finished }"
            :style="maskStyle"
        >
            <iframe
              class="bg-video"
              :style="videoStyle"
              :src="youtubeEmbedUrl"
              title="Background video"
              frameborder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowfullscreen
            ></iframe>
        </div>

        <img
            src="@/assets/logo-mask.svg"
            class="logo"
            :style="logoStyle"
            alt="Logo"
            draggable="false"
        />

        <div class="cursor-wrap" :style="cursorWrapStyle">
          <img
            src="@/assets/cursor.png"
            class="cursor-icon"
            :style="cursorStyle"
            alt="Indicador de rolagem"
            draggable="false"
          />
        </div>
    </section>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import logoMask from '@/assets/logo-mask.svg'

const props = defineProps({
  progress: {
    type: Number,
    default: 0
  }
})

const youtubeEmbedUrl =
  'https://www.youtube-nocookie.com/embed/4AJ_E2bV_cE?autoplay=1&mute=1&loop=1&playlist=4AJ_E2bV_cE&controls=0&modestbranding=1&rel=0&playsinline=1'

const finished = computed(() => props.progress >= 1)

const acceleratedProgress = computed(() => Math.pow(props.progress, 3))

const baseSize = 300;
const windowWidth = ref(0)

const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth || 0
}

onMounted(() => {
  updateWindowWidth()
  window.addEventListener('resize', updateWindowWidth)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWindowWidth)
})

// No maxScale, 10x o comprimento do logo deve ser igual à largura da tela.
const maxScale = computed(() => {
  if (windowWidth.value <= 0) return 1
  return windowWidth.value / (0.09 * baseSize)
})

const scale = computed(() => 
  1 + acceleratedProgress.value * (maxScale.value - 1)
)

const maskStyle = computed(() => ({
  WebkitMaskImage: props.progress >= 1 ? 'none' : `url(${logoMask})`,
  WebkitMaskSize: `${baseSize * scale.value}px`,
  WebkitMaskRepeat: 'no-repeat',
  WebkitMaskPosition: 'center',
  maskImage: props.progress >= 1 ? 'none' : `url(${logoMask})`,
  maskSize: `${baseSize * scale.value}px`,
}))

const logoStyle = computed(() => ({
  width: `${baseSize * scale.value}px`,
  opacity: 1 - props.progress
}))

const cursorOpacity = computed(() => Math.max(0, 1 - (props.progress / 0.3)))

const cursorWrapStyle = computed(() => ({
  top: `calc(50% + ${(baseSize * scale.value) / 2 + 28}px)`,
  opacity: cursorOpacity.value
}))

const cursorStyle = computed(() => ({
  width: `${baseSize * scale.value * 0.12}px`
}))



const bgStyle = computed(() => ({
  opacity: props.progress < 1 ? 1 : 0,
  pointerEvents: 'none'
}))

const videoStyle = computed(() => ({
  opacity: 1,
  pointerEvents: 'none'
}))
</script>

<style scoped>
.hero {
  position: sticky;
  inset: 0;
  height: 100vh;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
}

.hero-sticky {
  position: sticky;
  top: 0;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-video {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100vw;
  height: 56.25vw;
  min-width: 177.78vh;
  min-height: 100vh;
  transform: translate(-50%, -50%) scale(1.35);
  z-index: 1;
  border: 0;
  pointer-events: none;
}

.solid-bg {
  position: absolute;
  inset: 0;
  background: #0b1c2d;
  z-index: 1;
}

.video-mask {
  position: absolute;
  inset: 0;
  background: black;
  z-index: 1;

  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-repeat: no-repeat;
  mask-position: center;
}

.video-mask.released {
  -webkit-mask-image: none;
  mask-image: none;
}

.logo {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  max-width: none;
  z-index: 2;
  user-select: none;
  -webkit-user-drag: none;
}

.cursor-wrap {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  pointer-events: none;
}

.cursor-icon {
  display: block;
  height: auto;
  animation: cursor-bob 1.3s ease-in-out infinite;
  user-select: none;
  -webkit-user-drag: none;
}

@keyframes cursor-bob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(10px);
  }
}

@media (max-width: 768px) {
  .bg-video {
    transform: translate(-50%, -50%) scale(1.08);
  }

  .logo {
    width: 60vw;
  }
}
</style>
